// Auto-generated from docs/architecture/site-dna.md — do not edit manually
const content = `
# Site DNA Architecture

## Overview

Site DNA is the solar + battery storage optimization domain within the Vortx Flux platform. It enables users to configure a commercial/industrial site with photovoltaic (PV) arrays, battery energy storage systems (BESS), and backup generators, then run techno-economic optimization to find the lowest lifecycle-cost system design under real utility tariffs.

The domain answers the question: **Given a site's electric load, utility tariff, and available space/budget, what combination of solar, storage, and generators minimizes the lifecycle cost of electricity?**

Key capabilities:
- Scenario-based modeling with full CRUD operations
- Dual solver support: NREL REopt API (cloud) and Vortx Flux (self-hosted Julia LP/MILP)
- Real utility tariff lookup via URDB (Utility Rate Database)
- Custom tariff builder with TOU, tiered, and demand charge support
- Load profile upload (CSV) or generation from DOE Commercial Reference Buildings
- Post-optimization financial analysis including deal structuring (CapEx sale vs. ESA)
- Battery degradation modeling and outage survivability simulation
- Grid import/export limit constraints (Vortx Flux only)

## Architecture

\`\`\`
                     Frontend (React)
                    apps/site-dna-web
                          |
                    Firebase Auth JWT
                          |
                   Platform API (proxy)
              apps/vortx-platform-api
                          |
                 Site DNA API (Flask)
                  apps/site-dna-api
                    /            \\
           NREL REopt API    Vortx Flux Solver
          (cloud, REST)     apps/vortx-flux-solver
                             (Julia, Cloud Run)
                                  |
                            HiGHS LP/MILP
\`\`\`

### Request Flow

1. **Frontend** (\`site-dna-web\`) builds a scenario object with \`reopt_inputs\` containing PV, ElectricStorage, Generator, ElectricLoad, ElectricTariff, Financial, Site, and Settings.
2. **Platform API** (\`vortx-platform-api\`) authenticates the Firebase JWT and proxies the request to Site DNA API.
3. **Site DNA API** (\`site-dna-api\`) receives the optimization request, fetches the scenario from Firestore, preprocesses it (load scaling, PV sizing logic, battery config), sanitizes it for the chosen solver, and submits the job.
4. **Solver** (NREL REopt or Vortx Flux) solves the LP/MILP problem and returns optimal system sizes, dispatch schedules, and financial metrics.
5. **Site DNA API** polls for completion, post-processes results (degradation, survivability), uploads full results to Cloud Storage, and updates Firestore with KPIs.
6. **Frontend** polls the run status endpoint and renders results when complete.

### Data Storage

- **Firestore** (named database \`vortx-flux-site-dna\`): Stores user records, projects, scenarios, and optimization run metadata.
  - Path: \`users/{firebase_uid}/projects/{project_id}/scenarios/{scenario_id}/optimization_runs/{run_id}\`
- **Cloud Storage**: Stores full optimization results (8760 hourly time series data) to avoid Firestore document size limits.
- **In-memory**: The Julia solver stores jobs in a \`Dict{String, Dict}\` keyed by UUID, with a semaphore-bounded worker pool.

## API Routes

All routes are prefixed per blueprint. Authentication is via Firebase JWT passed as \`Authorization: Bearer <token>\`.

### Project Routes (\`/api/v1/projects\`)

| Method | Endpoint | Description |
|--------|----------|-------------|
| \`GET\` | \`/api/v1/projects\` | List all projects (own + team). Query param: \`include_scenarios=true\` |
| \`POST\` | \`/api/v1/projects\` | Create project. Body: \`{name, description, site_name, site_address, latitude, longitude}\` |
| \`GET\` | \`/api/v1/projects/<project_id>\` | Get project with scenarios |
| \`PUT\` | \`/api/v1/projects/<project_id>\` | Update project fields: \`name, description, site_name, site_address, latitude, longitude, default_settings\` |
| \`DELETE\` | \`/api/v1/projects/<project_id>\` | Delete project and all child scenarios/runs |
| \`PUT\` | \`/api/v1/projects/<project_id>/apps/<app_name>\` | Enable an app on a project |
| \`DELETE\` | \`/api/v1/projects/<project_id>/apps/<app_name>\` | Disable an app on a project |

### Scenario Routes (\`/api/v1/projects/<project_id>/scenarios\`)

| Method | Endpoint | Description |
|--------|----------|-------------|
| \`GET\` | \`.../scenarios\` | List scenarios in a project |
| \`POST\` | \`.../scenarios\` | Create scenario. Body includes \`reopt_inputs\` |
| \`GET\` | \`.../scenarios/<scenario_id>\` | Get scenario with optional run history |
| \`PUT\` | \`.../scenarios/<scenario_id>\` | Full update of scenario |
| \`PATCH\` | \`.../scenarios/<scenario_id>\` | Partial update (optimistic sync) |
| \`DELETE\` | \`.../scenarios/<scenario_id>\` | Delete scenario and its runs |
| \`POST\` | \`.../scenarios/<scenario_id>/duplicate\` | Duplicate a scenario |

### Optimization Routes

| Method | Endpoint | Description |
|--------|----------|-------------|
| \`POST\` | \`.../scenarios/<sid>/run\` | Submit optimization. Body: \`{optimization_engine: "nrel_reopt" \\| "vortx_flux"}\`. Returns 202 with \`run_id\` |
| \`GET\` | \`.../scenarios/<sid>/runs\` | List all runs for a scenario |
| \`GET\` | \`.../scenarios/<sid>/runs/<rid>/status\` | Poll run status. Returns \`pending\`, \`optimal\`, \`error\`, \`failed\` |
| \`GET\` | \`.../scenarios/<sid>/runs/<rid>/results\` | Get full results (alias for status when complete) |
| \`PATCH\` | \`.../scenarios/<sid>/runs/<rid>/financials\` | Save frontend-calculated financial data (CapEx, ESA, deal analysis) |
| \`GET\` | \`/api/v1/projects/<pid>/analysis\` | Cross-scenario comparison of all runs in a project |

### Variant Run Routes

| Method | Endpoint | Description |
|--------|----------|-------------|
| \`GET\` | \`.../scenarios/<sid>/variant-runs\` | List variant runs (sensitivity analysis) |
| \`POST\` | \`.../scenarios/<sid>/variant-runs\` | Create variant run with parameter overrides |
| \`DELETE\` | \`.../scenarios/<sid>/variant-runs\` | Delete all variant runs |
| \`DELETE\` | \`.../scenarios/<sid>/variant-runs/<rid>\` | Delete specific variant run |
| \`POST\` | \`.../scenarios/<sid>/promote-variant\` | Promote a variant run to become the primary run |

### Tariff Routes

| Method | Endpoint | Description |
|--------|----------|-------------|
| \`GET\` | \`/urdb?lat=&lon=\` | Fetch URDB tariffs for a location |
| \`GET\` | \`/urdb/<label>\` | Get detailed tariff by URDB label |
| \`GET\` | \`/api/tariffs?lat=&lon=\` or \`?address=\` | Normalized tariffs for frontend display |
| \`GET\` | \`.../projects/<pid>/tariffs\` | List project tariffs (custom + URDB used in scenarios) |
| \`POST\` | \`.../projects/<pid>/tariffs\` | Create custom tariff. Body: \`{name, utility, tariff_type, demand_charges, energy_charges}\` |
| \`GET\` | \`.../projects/<pid>/tariffs/<tid>\` | Get custom tariff |
| \`DELETE\` | \`.../projects/<pid>/tariffs/<tid>\` | Delete custom tariff |

### Load Profile Routes (\`/api/v1/load-profile\`)

| Method | Endpoint | Description |
|--------|----------|-------------|
| \`POST\` | \`/api/v1/load-profile/upload\` | Upload CSV load profile. Detects format, extracts 8760 hourly values, normalizes to Jan-Dec |

### Load Model Routes (\`/load_models\`)

| Method | Endpoint | Description |
|--------|----------|-------------|
| \`GET\` | \`/load_models\` | List available load model types (DOE reference buildings) |

### Equipment Routes (\`/api/v1/equipment\`)

| Method | Endpoint | Description |
|--------|----------|-------------|
| \`GET\` | \`/api/v1/equipment/<type>\` | List equipment by type (admin only) |

### Export Tariff Routes (\`/api/v1/export-tariffs\`)

NEM 3.0 export compensation rate lookup for California sites.

### CA DER Routes (\`/api/v1/ca-der\`)

California Departing Load and Standby charge calculation endpoints.

### Solver Admin Routes (\`/api/v1/solver\`)

| Method | Endpoint | Description |
|--------|----------|-------------|
| \`GET\` | \`/api/v1/solver/jobs\` | List optimization jobs (super admin only). Query: \`limit\`, \`engine\` |
| \`GET\` | \`/api/v1/solver/jobs/<run_uuid>\` | Get job details (super admin only) |

### Budget Routes (\`/api/v1/budgets\`)

Budget template CRUD for financial projections.

### Stats Routes (\`/api/v1/stats\`)

App-specific usage statistics.

### Vortx Flux Solver HTTP API

The Julia solver exposes a REST API on port 8000 (default):

| Method | Endpoint | Description |
|--------|----------|-------------|
| \`POST\` | \`/job\` | Submit optimization job. Body: full scenario JSON. Returns \`{job_id}\` |
| \`GET\` | \`/job/<id>/status\` | Poll job status |
| \`GET\` | \`/job/<id>/results\` | Get full results |
| \`GET\` | \`/health\` | Health check |

## Frontend Components

### Entry Point

- \`apps/site-dna-web/src/SiteDNAApp.jsx\` -- Main app component with routing (NOT \`App.jsx\`, which is legacy)

### Pages

All in \`apps/site-dna-web/src/pages/\`:

| File | Description |
|------|-------------|
| \`Dashboard.jsx\` | User dashboard with project list |
| \`Projects.jsx\` | Project list/grid view |
| \`ProjectAnalysis.jsx\` | Cross-scenario comparison page |
| \`ScenarioEditor.jsx\` | Main scenario configuration page (the core UI) |

### ScenarioEditor Architecture

\`ScenarioEditor.jsx\` is the primary page. It uses custom hooks for state management:

- \`useScenarioForm\` -- form field state (react-hook-form)
- \`useLoadProfile\` -- 8760 load data management
- \`useOptimization\` -- job submission and status polling
- \`useScenarioPayload\` -- builds the solver payload from form state
- \`useOptimizationSubmit\` -- handles submit flow
- \`useFinancialAnalysis\` -- post-optimization financial calculations
- \`useDirtyTracking\` -- optimistic sync dirty detection
- \`useFormValidation\` -- field validation
- \`useTariffSync\` -- syncs tariff selection with form state
- \`useBudget\` -- budget/CapEx template management
- \`useCommercialAssumptions\` -- deal analysis assumptions

The editor is organized into **7 tab sections**, each extracted to its own component:

### Scenario Sections

All in \`apps/site-dna-web/src/features/scenario/sections/\`:

| Section | Tab | Description |
|---------|-----|-------------|
| \`SiteSection.jsx\` | 0 - Site | Site location, address, coordinates, geocoding |
| \`UtilitySection.jsx\` | 1 - Utility | Tariff selection (URDB/custom), TOU visualization |
| \`LoadProfileSection.jsx\` | 2 - Load Profile | CSV upload, DOE reference building, load preview chart |
| \`SystemSection.jsx\` | 3 - System | DER configuration: Solar (PV), Battery (BESS), Generator. Grid constraints (import/export limits). Flux PhD topology profile selector |
| \`FinancialSection.jsx\` | 4 - Financial | Financial assumptions, escalation rates, ITC, analysis period |
| \`AnalysisSection.jsx\` | 5 - Analysis | Pre-optimization validation, scenario comparison |
| \`ResultsSection.jsx\` | 6 - Results | Multi-sub-tab results display: Performance, Deal Analysis, CA DER, Export |

### Key Shared Components (from \`@vortx/shared-ui\`)

| Component | Description |
|-----------|-------------|
| \`SectionCard\` | Consistent card wrapper for form sections |
| \`SystemDesignSummary\` | Displays optimal PV kW, battery kW/kWh, generator kW |
| \`PowerGraph\` | Time-series dispatch visualization |
| \`EmissionsPanel\` | CO2/NOx/SO2 emissions comparison |
| \`ExportDataTab\` | CSV/JSON export of results |
| \`DispatchRibbon3D\` | 3D dispatch visualization (three.js) |

### Site DNA Specific Components

In \`apps/site-dna-web/src/components/\`:

| Component | Description |
|-----------|-------------|
| \`DealAnalysisTab.jsx\` | CapEx sale vs. ESA comparison |
| \`BudgetTab.jsx\` | Budget template editor |
| \`CADERChargesAnalysis.jsx\` | CA DER departing load analysis |
| \`ScenarioAnalysis.jsx\` | Cross-scenario comparison |
| \`ResiliencePanel.jsx\` (in \`src/\`) | Outage survivability visualization |
| \`ResultsSummary.jsx\` (in \`src/\`) | KPI summary cards |
| \`MonthlyPerformanceTable.jsx\` (in \`src/\`) | Monthly energy/cost breakdown table |

### Scenario Sub-Components

In \`apps/site-dna-web/src/components/scenario/\`:

| Component | Description |
|-----------|-------------|
| \`SiteConfiguration\` | Address input, map, geocoding |
| \`SolarConfiguration\` | PV array type, tilt, azimuth, sizing mode (optimize/prescribed), existing PV |
| \`BatteryConfiguration\` | Battery kW/kWh, reserve %, grid charging, cycle cost |
| \`GeneratorConfiguration\` | Generator type, fuel cost, runtime settings |
| \`ScenarioFormProvider\` | React context provider for form state |
| \`FluxPhdProfileSelector\` | Topology efficiency profile selection |

## Solver

### Overview

The Vortx Flux solver is a Julia-based LP/MILP optimizer forked from NREL's REopt.jl. It uses the HiGHS solver (open-source) and runs as a Cloud Run service.

Module: \`VortxFlux\` (defined in \`apps/vortx-flux-solver/VortxFlux/src/VortxFlux.jl\`)

### Entry Points

- \`optimize(d::Dict)\` -- Main entry point. Creates a \`Scenario\`, builds BAU and optimal models, solves, and returns combined results.
- \`run_reopt(m, d)\` -- Lower-level: builds model from dict, solves, returns results.
- \`build_reopt!(m, p)\` -- Adds variables, constraints, and objective to a JuMP model.

### Decision Variables

Defined in \`add_variables!\` (\`core/reopt.jl\`):

| Variable | Type | Description |
|----------|------|-------------|
| \`dvSize[t]\` | Continuous | System size of technology t (kW) |
| \`dvPurchaseSize[t]\` | Continuous | New capacity to purchase beyond existing (kW) |
| \`dvGridPurchase[ts, tier]\` | Continuous | Grid power purchased per timestep per energy tier (kW) |
| \`dvRatedProduction[t, ts]\` | Continuous | Rated production of technology t at timestep ts (kW) |
| \`dvCurtail[t, ts]\` | Continuous | Curtailed production (kW) |
| \`dvProductionToStorage[b, t, ts]\` | Continuous | Power from tech t charging storage b (kW) |
| \`dvDischargeFromStorage[b, ts]\` | Continuous | Power discharged from storage b (kW) |
| \`dvGridToStorage[b, ts]\` | Continuous | Grid power charging storage b (kW) |
| \`dvStoredEnergy[b, ts]\` | Continuous | State of charge of storage b at timestep ts (kWh) |
| \`dvStoragePower[b]\` | Continuous | Power capacity of storage b (kW) |
| \`dvStorageEnergy[b]\` | Continuous | Energy capacity of storage b (kWh) |
| \`dvPeakDemandTOU[r, tier]\` | Continuous | Peak demand during TOU ratchet r (kW) |
| \`dvPeakDemandMonth[m, tier]\` | Continuous | Peak demand during month m (kW) |
| \`dvProductionToGrid[t, bin, ts]\` | Continuous | Export to grid by tech, export bin, timestep (kW) |
| \`binGenIsOnInTS[t, ts]\` | Binary | Generator on/off status per timestep |
| \`binNoGridPurchases[ts]\` | Binary | For simultaneous import/export prevention |
| \`dvFuelUsage[t, ts]\` | Continuous | Fuel consumption by fuel-burning tech (kWh) |

### Objective Function

The objective minimizes lifecycle cost (\`core/reopt.jl\`, line ~382):

\`\`\`
Min  Costs + ObjectivePenalties
\`\`\`

---
*This page is auto-generated from \`docs/architecture/\`. See the full document for complete details.*
`;

export default content;
